import clsx from 'clsx';
import Decimal from 'decimal.js';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlamingoFund, ProductCode, UnitCategoryCode } from 'funds-model';
import { useTheme } from '@material-ui/core/styles';
import Collapse from '@material-ui/core/Collapse';
import { Title } from '../../Title/Title';
import { Card } from '../../Card/Card';
import { Button, CustomButtonProps } from '../../Button/Button';
import { CollapseButton } from '../../CollapseButton/CollapseButton';
import { useIsMobile } from '../../utils/useIsMobile';
import { Currency } from '../../Currency/Currency';
import { ProfitLossBadge } from '../../ProfitLossBadge/ProfitLossBadge';
import { Percent } from '../../Percent/Percent';
import { LabelWithTooltip } from '../../LabelWithTooltip/LabelWithTooltip';
import { LabelWithBar } from '../../LabelWithBar/LabelWithBar';
import { Divider } from '../../Divider/Divider';
import { RiskProgressBar } from '../../RiskProgressBar/RiskProgressBar';
import FundListItemIcons from '../FundListItemIcons';
import Skeleton from '../../Skeleton/Skeleton';
import { toHashColorHexString } from '../../utils/colorUtils';
import { ROI } from '../fundListOptions';
import FundBlockedInfo from './FundBlockedInfo';
import ProcessMenu, { ProcessMenuProps } from '../../ProcessMenu/ProcessMenu';
import { AutoTestIdPrefix, getGenerateAutoTestId } from '../../utils/testAutomation.utils';
import { Typography } from '../../Typography/Typography';
import MultipleWordsTextWithArrow from '../../MultipleWordsTextWithArrow/MultipleWordsTextWithArrow';

import useStyles from './FundListItemWithDetails.style';

export interface ProcessMenuItem {
  title?: React.ReactNode;
  icon?: React.ReactNode;
  divider?: boolean;
  action?: () => void;
  className?: string;
  disabled?: boolean;
  hidden?: boolean;
  staticItem?: boolean;
  autoTestId?: string;
}

export interface FundDetailsDialogProps {
  fund: FlamingoFund;
  unitCategoryCode: UnitCategoryCode;
  onSelectFund?: (fund: FlamingoFund) => void;
  className?: string;
  isFundSelected?: boolean;
  isButtonDisabled?: boolean;
  customButton?: React.ReactNode;
  titleAsButton?: boolean;
  changeRateKey?: ROI;
  isFundInadequate?: boolean;
  fundValue?: Decimal;
  customTopBarActions?: React.ReactNode;
  customBottomBarActions?: React.ReactNode;
  hasDynamicUnitCategoryCode?: boolean;
  processLayout?: boolean;
  classes?: {
    bottomBar?: string;
    fundDetailsDialogHeader?: string;
  };
  dialogOpenButtonLabel?: React.ReactNode;
}

export interface FundListItemWithDetailsProps {
  fund: FlamingoFund;
  afsCode: string;
  value: Decimal;
  change: Decimal;
  participation: Decimal;
  lastPurchaseAmount?: Decimal;
  topInfo?: string;
  isKorpo?: boolean;
  futureAllocation?: Decimal;
  isFundInadequate: boolean;
  unitCategory: UnitCategoryCode;
  changeRateKey: ROI;
  productCode: ProductCode;
  isMifidDependent?: boolean;
  isFundBlocked: boolean;
  buyButtonDisabled?: boolean;
  setBuyButtonLoading?: (value: boolean) => void;
  buyButtonLabel?: React.ReactNode;
  className?: string;
  showProcessMenu?: boolean;
  onInternalNavigate?: (path: string) => void;
  renderFundDetailsDialog?: (props: FundDetailsDialogProps) => React.ReactNode;
  generateAutoTestId?: (prefix: AutoTestIdPrefix, label: string | undefined) => string | undefined;
  isDarkTheme?: boolean;
  isReady?: boolean;
  menuItems?: ProcessMenuItem[];
  showBuyButton?: boolean;
  onBuyClick?: () => void;
  allFunds?: FlamingoFund[];
  autoTestId?: string;
  isTestAutomationEnabled?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

interface BuyButtonProps {
  isLoading: boolean;
  disabled?: boolean;
  fundName: string;
  buttonType: CustomButtonProps['buttonType'];
  buttonVariant?: CustomButtonProps['buttonVariant'];
  buttonWidth?: string;
  onBuyClick: () => void;
  label?: React.ReactNode;
  isTestAutomationEnabled?: boolean;
  isAccent?: boolean;
  disableFullWidthOnMobile?: boolean;
}

export const BuyButton: React.FC<BuyButtonProps> = ({
  isLoading,
  disabled,
  fundName,
  label,
  buttonType,
  buttonVariant,
  buttonWidth,
  onBuyClick,
  isAccent = false,
  isTestAutomationEnabled = false,
  disableFullWidthOnMobile = false }) => {
  const c = useStyles({});
  const [t] = useTranslation('components');

  const generateAutoTestId = getGenerateAutoTestId(isTestAutomationEnabled);

  return (
    <Button
      className={c.selectButton}
      buttonType={buttonType}
      accent={isAccent}
      isLoading={isLoading}
      disabled={disabled}
      aria-label={t('product-account-row.buy-aria-label', { productName: fundName })}
      onClick={onBuyClick}
      autoTestId={generateAutoTestId?.(AutoTestIdPrefix.BUTTON, 'product-account-row.buy')}
      buttonVariant={buttonVariant}
      disableFullWidthOnMobile={disableFullWidthOnMobile}
      width={buttonWidth}
    >
      {label ?? t('product-account-row.buy')}
    </Button>
  );
};

const FundListItemWithDetails: React.FC<FundListItemWithDetailsProps> = ({
  fund,
  afsCode,
  value,
  change,
  participation,
  lastPurchaseAmount,
  topInfo,
  isKorpo = false,
  futureAllocation,
  isFundInadequate,
  unitCategory,
  changeRateKey,
  productCode,
  isMifidDependent = false,
  isFundBlocked,
  buyButtonDisabled,
  setBuyButtonLoading,
  buyButtonLabel,
  className,
  showProcessMenu = true,
  onInternalNavigate,
  renderFundDetailsDialog,
  generateAutoTestId,
  isDarkTheme = false,
  isReady = false,
  menuItems,
  showBuyButton,
  onBuyClick,
  allFunds,
  autoTestId,
  isTestAutomationEnabled = false,
  expanded,
  onExpandedChange }) => {
  const [t] = useTranslation('components');
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [isBuyButtonLoading] = useState(false);

  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = expanded !== undefined;
  const isAccordionExpanded = isControlled ? expanded : internalExpanded;

  const handleToggle = () => {
    const next = !isAccordionExpanded;
    if (!isControlled) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
  };
  const isProductPpePpiPpk = [ProductCode.ppe, ProductCode.ppi, ProductCode.ppk].includes(productCode);
  const shouldShowResult = productCode !== ProductCode.ppk;

  const fundWithPhotos =
    fund != null && allFunds != null && allFunds.length > 0
      ? {
          ...fund,
          fundPhoto: allFunds.find(f => f.id === fund.id)?.fundPhoto,
          fundPhotoType: allFunds.find(f => f.id === fund.id)?.fundPhotoType,
          isApiFundPhotoChecked: allFunds.find(f => f.id === fund.id)?.isApiFundPhotoChecked,
          fundManagersPhoto: allFunds.find(f => f.id === fund.id)?.fundManagersPhoto }
      : fund;

  const umbrellaColor = fundWithPhotos?.umbrella?.colorHex != null ? toHashColorHexString(fundWithPhotos.umbrella.colorHex) : theme.color.violets[50];

  const c = useStyles({topInfo});

  const titleNode = (
    <MultipleWordsTextWithArrow
      text={fund?.name}
      classNameWrapper={c.textWrapper}
      classNameText={c.text}
      classNameIcon={c.icon}
    />
  );

  const isPensionProduct = [ProductCode.ike, ProductCode.ikze].includes(productCode);
  const shouldShowKorpoInfo = Boolean(topInfo && isKorpo && isPensionProduct);

  const displayTopInfo = shouldShowKorpoInfo
    ? `${topInfo} - ${t('wallet.korpo')}`
    : topInfo;

  const renderBuyButton = (buttonType: CustomButtonProps['buttonType']) => {
    if (!isReady) {
      return (
        <Skeleton className={c.actionButtonSkeleton} variant="rect">
          <Button className={c.selectButton} buttonType={buttonType} autoTestId={generateAutoTestId?.(AutoTestIdPrefix.BUTTON, 'product-account-row.buy')}>
            {t('product-account-row.buy')}
          </Button>
        </Skeleton>
      );
    }

    return (
      <BuyButton
        buttonType={buttonType}
        buttonVariant={"medium"}
        isLoading={isBuyButtonLoading}
        disabled={!isBuyButtonLoading && buyButtonDisabled}
        fundName={fund.name}
        onBuyClick={onBuyClick ?? (() => {})}
        label={buyButtonLabel}
        isTestAutomationEnabled={isTestAutomationEnabled}
      />
    );
  };

  const renderProcessMenu = (buttonVariant: ProcessMenuProps['buttonVariant']) => {
    if (isFundBlocked) {
      return (
        <FundBlockedInfo
          fundName={fund.name}
          fundAdditionalInfo={fund.additionalTab ? { content: fund.additionalTab.content || '' } : undefined}
          onInternalNavigate={onInternalNavigate}
        />
      );
    }

    const items = menuItems ?? [];
    const autoTestIdText = items.length === 1
      ? `product-funds-table.processes.conversion-change-${fund.name}`
      : `product-account-row.orders-${fund.name}`;

    return (
        <ProcessMenu
            title={t('product-account-row.manage')}
            menuItems={items}
            autoTestId={generateAutoTestId?.(AutoTestIdPrefix.BUTTON, autoTestIdText)}
            buttonVariant={buttonVariant}
            allowSingleItemAsButton={true}
            contextName={fund.name}
            generateAutoTestId={generateAutoTestId}
            isDarkTheme={isDarkTheme}
        />
    );
  };

  const renderCustomTopBarActions = () => (
    <div className={c.topBarActionsBox}>
      {renderProcessMenu(isMobile ? 'ghost-button' : 'full-chevron')}
      {showBuyButton && renderBuyButton('primary')}
    </div>
  );

  const renderCustomBottomBarActions = () => {
    if (!showBuyButton) {
      return undefined;
    }
    return (
      <div className={c.bottomBarActionsBox}>
        {renderBuyButton('primary')}
      </div>
    );
  };

  const renderActionButtons = () => (
    <div className={c.actionWrapper}>
      {showBuyButton && renderBuyButton('secondary')}
      {showProcessMenu && renderProcessMenu('full-chevron')}
    </div>
  );

  const renderExpandAction = () => (
    <div className={c.accordionExpandAction}>
      <CollapseButton
        className={c.accordionTitle}
        onClick={handleToggle}
        expanded={isAccordionExpanded}
        size='medium'
      >
        {isAccordionExpanded ? t('fund-list.hide-details') : t('fund-list.show-details')}
      </CollapseButton>
    </div>
  );

  return (
    <Card className={clsx(c.root, className)} hoverable autoTestId={autoTestId} overflow="none" size='equal'>
      <div className={c.detailsWrapper}>
        <div className={c.titleWrapper}>
          {fund != null
            ? (
              <>
                <div className={c.titleAndIconWrapper}>
              {displayTopInfo && <Typography textColor="greys.80" variant="body2" className={c.topInfo}>{displayTopInfo}</Typography>}
              <FundListItemIcons
                fund={fundWithPhotos}
                isFundInadequate={isMifidDependent && isFundInadequate}
                umbrellaColor={umbrellaColor}
                fundValue={value}
              />
                  {renderFundDetailsDialog ? renderFundDetailsDialog({
                    fund: fundWithPhotos,
                    fundValue: value,
                    unitCategoryCode: unitCategory,
                    isFundInadequate: isMifidDependent && isFundInadequate,
                    className: c.button,
                    customButton: <Title
                                    title={titleNode}
                                    titleComponent="h4"
                                    titleVariant="h4"
                                    titleFontWeight="bold"
                                  />,
                    titleAsButton: true,
                    changeRateKey,
                    customTopBarActions: renderCustomTopBarActions(),
                    customBottomBarActions: renderCustomBottomBarActions() }) : (
                    <Title
                        title={titleNode}
                        titleComponent="h4"
                        titleVariant="h4"
                        titleFontWeight="bold"
                      />
                    )}
                  {fund.previousName && <div className={c.oldName}>{fund.previousName}</div>}
                  {!isMobile && renderExpandAction()}
                </div>
                {isMobile && (
                  <>
                    {(showBuyButton || showProcessMenu) && renderActionButtons()}
                    <Divider fullWidth size="small" />
                  </>
                )}
                {lastPurchaseAmount != null ? (
                <div className={c.valueBox}>
                    <LabelWithTooltip
                      labelText={t('recently-purchased.last-purchase')}
                      title={t('recently-purchased.last-purchase-tooltip')}
                      size="s"
                      color="graphite"
                    />
                    <Currency valueFontWeight="bold" value={lastPurchaseAmount} valueSize={isMobile ? 's' : 'base'} codeSize={isMobile ? 's' : 'base'} />
                  </div>
                ) : (
                  <div className={c.valueBoxesWrapper}>
                    <div className={c.valueBox}>
                      <LabelWithTooltip
                        labelText={t('product-account-row.value')}
                        title={t('product-account-row.value-tooltip')}
                        size="s"
                        color="graphite"
                      />
                      <Currency valueFontWeight="bold" value={value} valueSize={isMobile ? 's' : 'base'} codeSize={isMobile ? 's' : 'base'} />
                    </div>
                    {shouldShowResult && (
                      <div className={c.valueBox}>
                        <LabelWithTooltip
                          labelText={t('product-account-row.result')}
                          title={t('product-account-row.result-tooltip')}
                          size="s"
                          color="graphite"
                        />
                        <ProfitLossBadge value={change} />
                      </div>
                    )}
                  </div>
                )}
                {isMobile && <Divider fullWidth size="small" />}
                {!isMobile && renderActionButtons()}
              </>
            )
            : <span className={c.noFundInfoRow}>{t('product-funds-table.no-data-for-afs', { afs: afsCode })}</span>}
        </div>
      </div>
      <Collapse in={isAccordionExpanded}>
        <div className={c.detailsContent}>
          {fund != null ? (
              <>
          <div className={c.infoBox}>
            <LabelWithTooltip
              labelText={t('fund-list.fundType')}
              preTitle={fund.fundType.tooltipTitle}
              title={fund.fundType.description}
              size="s"
              color="graphite"
            />
            <LabelWithBar
              text={fund.fundType.name}
              color={toHashColorHexString(fund?.umbrella?.colorHex)}
              size='big'
            />
          </div>
          <div className={c.infoBox}>
            <LabelWithTooltip
              labelText={`${t('fund-list.risk')}: ${fund.risk.filterLabel}`}
              title={t('fund-details-dialog.risk-tooltip')}
              size="s"
              color="graphite"
            />
            <RiskProgressBar
              riskValue={fund.risk.value}
              withRiskValueLabel
            />
          </div>
          {isMobile && <Divider fullWidth size="small" />}
          {lastPurchaseAmount == null && (
            <>
              <div className={c.infoBox}>
                <LabelWithTooltip
                  labelText={t('product-funds-table.share')}
                  title={t('product-funds-table.share-tooltip')}
                  size="s"
                  color="graphite"
                />
                <Percent percent={participation} suffix={t('product-funds-table.share-suffix')} />
              </div>
              {isProductPpePpiPpk && (
                <div className={c.infoBox}>
                  <LabelWithTooltip
                    labelText={t('product-account-row.future-allocation-ppk')}
                    title={t('product-funds-table.future-allocation-ppk-tooltip')}
                    size="s"
                    color="graphite"
                  />
                  <Percent percent={futureAllocation} suffix={t('product-funds-table.future-allocation-ppe-ppi-suffix')} />
                </div>
              )}
              {isMobile && <Divider fullWidth size="small" />}
            </>
          )}</>) : <span className={c.noFundInfoRow}>{t('product-funds-table.no-data-for-afs', { afs: afsCode })}</span>}
        </div>
      </Collapse>
      {isMobile && renderExpandAction()}

    </Card>
  );
};

export default FundListItemWithDetails;
